import { Download, Edit, Eye, History, MapPin, X } from 'lucide-react';
import React, { memo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { ProjectPhoto } from '../../types';
import { formatFileSize } from '../../utils/photoOptimizations';

interface PhotoDetailsDrawerProps {
  photo: ProjectPhoto | null;
  isOpen: boolean;
  onClose: () => void;
  onDownload?: (photo: ProjectPhoto) => void;
  onDelete?: (photo: ProjectPhoto) => void;
  onVersionHistory?: (taggingId: string) => void;
  onAnnotationToggle?: () => void;
  getCategoryInfo: (category: string) => { label: string; color: string };
  formatDate: (dateString: string) => string;
  permissions?: {
    download?: boolean;
    delete?: boolean;
    edit?: boolean;
    view_history?: boolean;
    annotate?: boolean;
  };
  showAnnotationMode?: boolean;
}

export const PhotoDetailsDrawer: React.FC<PhotoDetailsDrawerProps> = memo(
  ({
    photo,
    isOpen,
    onClose,
    onDownload,
    onDelete: _onDelete,
    onVersionHistory,
    onAnnotationToggle,
    getCategoryInfo,
    formatDate,
    permissions = {},
    showAnnotationMode = false,
  }) => {
    const { t } = useTranslation();

    if (!isOpen || !photo) return null;

    const drawerContent = (
      <>
        {/* Backdrop */}
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-[110]" onClick={onClose} />

        {/* Drawer */}
        <div className="fixed top-6 right-6 bottom-6 w-full max-w-2xl bg-white shadow-xl rounded-xl border border-gray-200 z-[120] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-white rounded-t-xl">
            <div className="flex items-center space-x-3">
              <div className="bg-red-100 p-2 rounded-lg">
                <Eye className="h-6 w-6 text-red-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('photos.details.photoDetails')}
                </h3>
                <p className="text-sm text-gray-600">View and manage photo information</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-red-500 transition-colors p-2 hover:bg-red-50 rounded-lg disabled:opacity-50"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Photo Preview */}
          <div className="bg-gray-900 flex items-center justify-center p-6 flex-shrink-0">
            <img
              src={photo.file_url}
              alt={photo.title}
              className="max-w-full max-h-64 object-contain rounded-xl shadow-lg"
            />
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-6 space-y-6">
              {/* Photo Title & Description */}
              <div className="space-y-3">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 leading-tight">
                    {photo.title}
                  </h3>
                  {photo.description && (
                    <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                      {photo.description}
                    </p>
                  )}
                </div>
              </div>

              {/* Photo Details Grid */}
              <div className="space-y-4">
                <h4 className="text-base font-semibold text-gray-900 border-b border-gray-100 pb-2">
                  {t('photos.details.photoDetails')}
                </h4>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="space-y-1">
                    <span className="font-medium text-gray-700 block">
                      {t('photos.details.taggingId')}
                    </span>
                    <p className="text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded">
                      {photo.tagging_id || 'N/A'}
                    </p>
                  </div>

                  <div className="space-y-1">
                    <span className="font-medium text-gray-700 block">
                      {t('photos.details.number')}
                    </span>
                    <p className="text-gray-900">
                      {photo.number_on_image ? `#${photo.number_on_image}` : 'N/A'}
                    </p>
                  </div>

                  <div className="space-y-1">
                    <span className="font-medium text-gray-700 block">
                      {t('photos.details.category')}
                    </span>
                    <span
                      className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${getCategoryInfo(photo.category).color}`}
                    >
                      {getCategoryInfo(photo.category).label}
                    </span>
                  </div>

                  <div className="space-y-1">
                    <span className="font-medium text-gray-700 block">
                      {t('photos.details.version')}
                    </span>
                    <p className="text-gray-900">v{photo.version || '1.0'}</p>
                  </div>
                </div>
              </div>

              {/* Location Information */}
              <div className="space-y-4">
                <h4 className="text-base font-semibold text-gray-900 border-b border-gray-100 pb-2">
                  {t('photos.details.location')}
                </h4>

                <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 space-y-3">
                  {photo.location?.site && (
                    <div>
                      <span className="text-sm font-medium text-gray-700">Site:</span>
                      <p className="text-gray-900 font-medium">{photo.location.site}</p>
                    </div>
                  )}

                  {photo.location?.building && (
                    <div>
                      <span className="text-sm font-medium text-gray-700">Building:</span>
                      <p className="text-gray-900">{photo.location.building}</p>
                    </div>
                  )}

                  {photo.location?.floor && (
                    <div>
                      <span className="text-sm font-medium text-gray-700">Floor:</span>
                      <p className="text-gray-900">{photo.location.floor}</p>
                    </div>
                  )}

                  {photo.location?.room && (
                    <div>
                      <span className="text-sm font-medium text-gray-700">Room:</span>
                      <p className="text-gray-900">{photo.location.room}</p>
                    </div>
                  )}

                  {photo.location?.gps && (
                    <div>
                      <span className="text-sm font-medium text-gray-700">GPS Coordinates:</span>
                      <p className="text-red-600 cursor-pointer hover:text-red-700 hover:underline flex items-center mt-1">
                        <MapPin className="h-3 w-3 mr-1" />
                        {photo.location.gps}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Tags */}
              {photo.tags && photo.tags.length > 0 && (
                <div className="space-y-4">
                  <h4 className="text-base font-semibold text-gray-900 border-b border-gray-100 pb-2">
                    {t('photos.details.tags')}
                  </h4>

                  <div className="flex flex-wrap gap-2">
                    {photo.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-red-50 text-red-700 text-sm font-medium rounded-full border border-red-100"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Upload Information */}
              <div className="space-y-4">
                <h4 className="text-base font-semibold text-gray-900 border-b border-gray-100 pb-2">
                  {t('photos.details.uploadInformation')}
                </h4>

                <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 space-y-3 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-700">
                      {t('photos.details.uploaded')}:
                    </span>
                    <span className="text-gray-900 font-medium">
                      {formatDate(photo.uploaded_at)}
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-700">{t('photos.details.by')}:</span>
                    <span className="text-gray-900">
                      {photo.uploaded_by_name || 'Unknown User'}
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-700">{t('photos.details.size')}:</span>
                    <span className="text-gray-900 font-mono">
                      {formatFileSize(photo.file_size || 0)}
                    </span>
                  </div>

                  {photo.metadata?.width && photo.metadata?.height && (
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-700">
                        {t('photos.details.dimensions')}:
                      </span>
                      <span className="text-gray-900 font-mono">
                        {photo.metadata.width} × {photo.metadata.height}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Sticky Footer */}
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex-shrink-0">
              <div className="space-y-3">
                <div className="flex space-x-3">
                  {permissions.download && onDownload && (
                    <button
                      onClick={() => onDownload(photo)}
                      className="flex-1 inline-flex items-center justify-center px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      {t('photos.details.download')}
                    </button>
                  )}

                  {permissions.view_history && onVersionHistory && (
                    <button
                      onClick={() => onVersionHistory(photo.tagging_id)}
                      className="flex-1 inline-flex items-center justify-center px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"
                    >
                      <History className="h-4 w-4 mr-2" />
                      {t('photos.details.history')}
                    </button>
                  )}
                </div>

                {permissions.annotate && onAnnotationToggle && (
                  <button
                    onClick={onAnnotationToggle}
                    className="w-full inline-flex items-center justify-center px-4 py-2.5 border border-red-500 rounded-xl text-sm font-semibold text-white bg-red-500 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    {showAnnotationMode
                      ? t('photos.details.exitAnnotation')
                      : t('photos.details.addAnnotations')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    );

    return createPortal(drawerContent, document.body);
  }
);

PhotoDetailsDrawer.displayName = 'PhotoDetailsDrawer';
